import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { isPremium, isAdmin } from '../utils/userAccess';
import DogIcon from '../assets/icons/dog.svg';
import DogFaceIcon from '../assets/icons/dog-face-svgrepo-com.svg';
import DogOrigamiIcon from '../assets/icons/dog-origami-paper-svgrepo-com.svg';
import DogIcon1 from '../assets/icons/dog-svgrepo-com (1).svg';
import DogFoodIcon from '../assets/icons/dog-food-pictogram-2-svgrepo-com.svg';
// import Dog5Icon from '../assets/icons/dog-5-svgrepo-com.svg';
import DogBoneIcon from '../assets/icons/dog-bone-svgrepo-com.svg';
import Dog13Icon from '../assets/icons/dog-13-svgrepo-com.svg';
import PawPrint from '../assets/icons/paw-print.svg';
import StarIcon from '../assets/icons/star.svg';
import GearIcon from '../assets/icons/gear.svg';
import * as dogsApi from '../api/dogs';
import * as userApi from '../api/user';
import * as favoritesApi from '../api/favorites';
import type { Dog } from '../api/dogs';
import type { Favorite } from '../api/favorites';
import FlashMessage from '../components/FlashMessage';
import { BACKEND_URL } from '../config';
import './Profile.css';
import { isMobileView } from '../utils/deviceUtils';

// Icon name mapping: backend value -> icon import
const ICON_MAP: Record<string, any> = {
  'dog-face': DogFaceIcon,
  'dog-origami': DogOrigamiIcon,
  'dog-profile': DogIcon1,
  'dog-food': DogFoodIcon,
  'dog-bone': DogBoneIcon,
  'dog-paw': Dog13Icon,
};

// Avatar icon options with backend-friendly IDs
const AVATAR_ICONS = [
  { id: 'dog-face', src: DogFaceIcon, label: 'Dog Face' },
  { id: 'dog-origami', src: DogOrigamiIcon, label: 'Origami Dog' },
  { id: 'dog-profile', src: DogIcon1, label: 'Dog Profile' },
  { id: 'dog-food', src: DogFoodIcon, label: 'Dog Bowl' },
  { id: 'dog-bone', src: DogBoneIcon, label: 'Dog Bone' },
  { id: 'dog-paw', src: Dog13Icon, label: 'Dog Paw' },
];

// Warm, soft colors that match the brand
const AVATAR_COLORS = [
  '#E57373',
  '#F4A261 ',
  '#EAD7C3',
  '#8FBF9F',
  '#B08968',
  '#D8A7B1',
];

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = isMobileView();
  const { user, logout, refreshUser, isLoading } = useAuth();
  const [dogs, setDogs] = useState<Dog[]>([]);
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [loading, setLoading] = useState(true);
  const [favoritesLoading, setFavoritesLoading] = useState(true);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const lastSubmitTime = React.useRef<number>(0);
  const [showDogModal, setShowDogModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [dogToDelete, setDogToDelete] = useState<number | null>(null);
  const [editingDog, setEditingDog] = useState<Dog | null>(null);
  const [flashMessage, setFlashMessage] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [userForm, setUserForm] = useState({
    display_name: '',
    first_name: '',
    last_name: '',
    avatar_icon: 'dog-face',
    avatar_color: '#FFE5C2',
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    password_confirmation: '',
  });
  const [dogForm, setDogForm] = useState({
    name: '',
    breed: '',
    age: '',
    weight: '',
    body_condition: '',
    meals_per_day: '',
    allergies: '',
  });

  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login?redirectTo=/profile');
    }
  }, [user, navigate, isLoading]);

  // Fetch dogs on mount
  useEffect(() => {
    if (user) {
      loadDogs();
      loadFavorites();
    }
  }, [user]);

  const loadDogs = async () => {
    try {
      setLoading(true);
      const fetchedDogs = await dogsApi.getDogs();
      setDogs(fetchedDogs);
    } catch (err) {
      console.error('Failed to load dogs:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    try {
      setFavoritesLoading(true);
      const fetchedFavorites = await favoritesApi.getFavorites();
      setFavorites(fetchedFavorites);
    } catch (err) {
      console.error('Failed to load favorites:', err);
    } finally {
      setFavoritesLoading(false);
    }
  };

  const handleAddDog = () => {
    setEditingDog(null);
    setDogForm({
      name: '',
      breed: '',
      age: '',
      weight: '',
      body_condition: '',
      meals_per_day: '',
      allergies: '',
    });
    setShowDogModal(true);
  };

  const handleEditDog = (dog: Dog) => {
    setEditingDog(dog);
    setDogForm({
      name: dog.name || '',
      breed: dog.breed || '',
      age: dog.age?.toString() || '',
      weight: dog.weight?.toString() || '',
      body_condition: dog.body_condition || '',
      meals_per_day: dog.meals_per_day?.toString() || '',
      allergies: dog.allergies ? dog.allergies.join(', ') : '',
    });
    setShowDogModal(true);
  };

  const handleSaveDog = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmittingForm) return;
    
    // Throttle: prevent submissions within 1 second of last submission
    const now = Date.now();
    if (now - lastSubmitTime.current < 1000) {
      return;
    }
    lastSubmitTime.current = now;
    
    setIsSubmittingForm(true);
    try {
      // Convert allergies string to array
      const allergyArray = dogForm.allergies
        .split(/[,]+/)
        .map(s => s.trim())
        .filter(Boolean);

      const params = {
        name: dogForm.name,
        breed: dogForm.breed.trim() || undefined,
        age: dogForm.age ? parseInt(dogForm.age) : undefined,
        weight: dogForm.weight ? parseFloat(dogForm.weight) : undefined,
        body_condition: dogForm.body_condition || 'ideal',
        meals_per_day: dogForm.meals_per_day ? parseInt(dogForm.meals_per_day) : undefined,
        allergies: allergyArray, // Always send the array, even if empty
      };


      if (editingDog) {
        await dogsApi.updateDog(editingDog.id, params);
      } else {
        await dogsApi.createDog(params);
      }

      setShowDogModal(false);
      await loadDogs();
      setFlashMessage({ message: editingDog ? 'Dog updated successfully!' : 'Dog added successfully!', type: 'success' });
    } catch (err) {
      console.error('Failed to save dog:', err);
      setFlashMessage({ message: 'Failed to save dog. Please try again.', type: 'error' });
    } finally {
      setIsSubmittingForm(false);
    }
  };

  // Check if form is valid (all required fields filled)
  const isFormValid = () => {
    const weightNum = parseFloat(dogForm.weight);
    const mealsNum = parseInt(dogForm.meals_per_day);
    return (
      dogForm.name.trim() !== '' &&
      dogForm.age !== '' &&
      dogForm.weight !== '' &&
      weightNum >= 0.5 &&
      weightNum <= 330 &&
      dogForm.body_condition !== '' &&
      dogForm.meals_per_day !== '' &&
      mealsNum >= 1 &&
      mealsNum <= 4
    );
  };

  const handleDeleteDog = async (dogId: number) => {
    setDogToDelete(dogId);
    setShowDeleteModal(true);
  };

  const confirmDeleteDog = async () => {
    if (!dogToDelete || isSubmittingForm) return;

    // Throttle: prevent submissions within 1 second of last submission
    const now = Date.now();
    if (now - lastSubmitTime.current < 1000) {
      return;
    }
    lastSubmitTime.current = now;

    setIsSubmittingForm(true);
    try {
      await dogsApi.deleteDog(dogToDelete);
      await loadDogs();
      setFlashMessage({ message: 'Dog removed successfully!', type: 'success' });
    } catch (err) {
      console.error('Failed to delete dog:', err);
      setFlashMessage({ message: 'Failed to delete dog. Please try again.', type: 'error' });
    } finally {
      setIsSubmittingForm(false);
      setShowDeleteModal(false);
      setDogToDelete(null);
    }
  };

  if (!user) {
    return null; // Will redirect
  }

  // Get initials for avatar
  const getInitials = (email?: string) => {
    if (!email) return '??';
    const parts = email.split('@')[0].split('.');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  const handleLogout = async () => {
    setFlashMessage({ message: 'You have been logged out successfully', type: 'success' });
    setTimeout(async () => {
      await logout();
      navigate('/');
    }, 1000);
  };

  const handleEditUser = () => {
    setUserForm({
      display_name: user?.display_name || '',
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      avatar_icon: user?.avatar_icon || 'dog-face',
      avatar_color: user?.avatar_color || '#FFE5C2',
    });
    setShowUserModal(true);
  };

  const handleSaveUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmittingForm) return;
    
    // Throttle: prevent submissions within 1 second of last submission
    const now = Date.now();
    if (now - lastSubmitTime.current < 1000) {
      return;
    }
    lastSubmitTime.current = now;
    
    setIsSubmittingForm(true);
    try {
      await userApi.updateProfile({
        display_name: userForm.display_name,
        first_name: userForm.first_name,
        last_name: userForm.last_name,
        avatar_icon: userForm.avatar_icon,
        avatar_color: userForm.avatar_color,
      });
      setShowUserModal(false);
      // Refresh user data from backend
      await refreshUser();
      setFlashMessage({ message: 'Profile updated successfully!', type: 'success' });
    } catch (err) {
      console.error('Failed to save user profile:', err);
      setFlashMessage({ message: 'Failed to save profile. Please try again.', type: 'error' });
    } finally {
      setIsSubmittingForm(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmittingForm) return;
    
    // Throttle: prevent submissions within 1 second of last submission
    const now = Date.now();
    if (now - lastSubmitTime.current < 1000) {
      return;
    }
    lastSubmitTime.current = now;
    
    setIsSubmittingForm(true);
    try {
      await userApi.changePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
        password_confirmation: passwordForm.password_confirmation,
      });
      setShowPasswordModal(false);
      setPasswordForm({
        current_password: '',
        new_password: '',
        password_confirmation: '',
      });
      setFlashMessage({ message: 'Password changed successfully!', type: 'success' });
    } catch (err) {
      console.error('Failed to change password:', err);
      setFlashMessage({ message: 'Failed to change password. Please check your current password and try again.', type: 'error' });
    } finally {
      setIsSubmittingForm(false);
    }
  };

  const isPasswordFormValid = () => {
    return (
      passwordForm.current_password.trim() !== '' &&
      passwordForm.new_password.trim() !== '' &&
      passwordForm.password_confirmation.trim() !== '' &&
      passwordForm.new_password === passwordForm.password_confirmation
    );
  };

  return (
    <div className="profile-page">
      {flashMessage && (
        <FlashMessage
          message={flashMessage.message}
          type={flashMessage.type}
          onClose={() => setFlashMessage(null)}
        />
      )}
      <div className="profile-container">
        <h1 className="profile-heading">My Profile</h1>

        {/* User Profile Section */}
        <section className="profile-section">
          <div className="profile-header">
            <div className="profile-avatar" style={{ 
              background: (() => {
                const color = user.avatar_color || '#FFE5C2';
                return color;
              })(),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {user.avatar_icon && ICON_MAP[user.avatar_icon] ? (
                <img 
                  src={ICON_MAP[user.avatar_icon]} 
                  alt="Avatar" 
                  style={{ width: '60%', height: '60%', objectFit: 'contain' }}
                />
              ) : (
                getInitials(user.email)
              )}
            </div>
            <div className="profile-info">
              <h2 className="profile-name">
                {user.display_name || user.first_name || user.name || (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : 'User')}
              </h2>
              <p className="profile-email">{user.email}</p>
            </div>
            <button className="btn-edit-profile" onClick={handleEditUser}>Edit Profile</button>
          </div>
        </section>

        {/* My Dog Section - Premium/Admin Only */}
        {(isPremium(user) || isAdmin(user)) && (
          <section className="profile-section">
            <h2 className="section-title">
              <img src={PawPrint} alt="" style={{ width: '24px', height: '24px', marginRight: '8px', verticalAlign: 'middle' }} />
              My {dogs.length === 1 ? 'Dog' : 'Dogs'}
            </h2>
            {loading ? (
              <p>Loading...</p>
            ) : dogs.length === 0 ? (
              <div className="no-dogs">
                <p>No dogs added yet. Add your first dog to get started!</p>
              </div>
            ) : (
              dogs.map((dog) => (
                <div key={dog.id} className="dog-card">
                  <img src={DogIcon} alt="Dog" className="dog-icon-svg" />
                  <div className="dog-info">
                    <h3 className="dog-name">{dog.name}</h3>
                    <p className="dog-details">
                      {[dog.breed, dog.age && `${dog.age} years`, dog.weight && `${dog.weight} lbs`]
                        .filter(Boolean)
                        .join(' • ')}
                    </p>
                    {dog.body_condition && (
                      <p className="dog-body-condition">Body Condition: {dog.body_condition}</p>
                    )}
                    {dog.meals_per_day && (
                      <p className="dog-meals">Meals Per Day: {dog.meals_per_day}</p>
                    )}
                    {dog.allergies && dog.allergies.length > 0 && (
                      <p className="dog-allergies">Allergies: {dog.allergies.join(', ')}</p>
                    )}
                  </div>
                  <div className="dog-actions">
                    <button 
                      className="btn-secondary btn-small"
                      onClick={() => handleEditDog(dog)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn-delete btn-small"
                      onClick={() => handleDeleteDog(dog.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
            <button className="btn-secondary" onClick={handleAddDog}>
              {dogs.length === 0 ? 'Add Dog' : 'Add Another Dog'}
            </button>
          </section>
        )}

        {/* Favorites Section - Premium/Admin Only */}
        {(isPremium(user) || isAdmin(user)) && (
          <section className="profile-section">
            <h2 className="section-title">
              <img src={StarIcon} alt="" style={{ width: '24px', height: '24px', marginRight: '8px', verticalAlign: 'middle' }} />
              Favorites
            </h2>
            {favoritesLoading ? (
              <p>Loading...</p>
            ) : favorites.length === 0 ? (
              <p className="favorites-empty-message">No favorite recipes yet. Start exploring recipes and add them to your favorites!</p>
            ) : (
              <div className="favorites-scroll-container">
                {favorites.map((favorite) => (
                  <div 
                    key={favorite.id} 
                    className="favorite-card"
                    onClick={() => favorite.recipe?.slug && navigate(`/recipes/${favorite.recipe.slug}`)}
                  >
                    {favorite.recipe?.photo_url ? (
                      <div className="favorite-card-image-container">
                        <img 
                          src={`${BACKEND_URL}${favorite.recipe.photo_url}`} 
                          alt={favorite.recipe.title}
                          className="favorite-card-image"
                        />
                      </div>
                    ) : (
                      <div className="favorite-card-placeholder">
                        <span className="favorite-card-placeholder-icon">🍽️</span>
                      </div>
                    )}
                    <div className="favorite-card-content">
                      <h3 className="favorite-card-title">
                        {favorite.recipe?.title || 'Recipe'}
                      </h3>
                      {favorite.recipe?.description && (
                        <p className="favorite-card-description">
                          {favorite.recipe.description.substring(0, 80)}...
                        </p>
                      )}
                      {(favorite.recipe?.prep_time || favorite.recipe?.cook_time) && (
                        <p className="favorite-card-meta">
                          {favorite.recipe.prep_time && `Prep: ${favorite.recipe.prep_time} min`}
                          {favorite.recipe.prep_time && favorite.recipe.cook_time && ' • '}
                          {favorite.recipe.cook_time && `Cook: ${favorite.recipe.cook_time} min`}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Account Section */}
        <section className="profile-section">
          <h2 className="section-title">
            <img src={GearIcon} alt="" style={{ width: '24px', height: '24px', marginRight: '8px', verticalAlign: 'middle' }} />
            Account
          </h2>
          <div className="account-info">
            <p className="account-email">
              <strong>Email:</strong> {user.email}
            </p>
          </div>
          <div className="account-actions">
            <button className="btn-secondary" onClick={() => setShowPasswordModal(true)}>Change Password</button>
            <button className="btn-logout" onClick={handleLogout}>
              Log out
            </button>
          </div>
        </section>
      </div>


    {(isMobile) && (
        <button
            type="button"
            className="btn-primary"
            onClick={() => {
                navigate('/settings');
            }}
            >
            Settings
        </button>
    )}

      {/* Dog Modal */}
      {showDogModal && (
        <div className="modal-overlay" onClick={() => setShowDogModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingDog ? 'Edit Dog' : 'Add Dog'}</h2>
              <button 
                className="modal-close"
                onClick={() => setShowDogModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSaveDog}>
              <div className="form-group">
                <label htmlFor="dog-name">Name *</label>
                <input
                  id="dog-name"
                  type="text"
                  value={dogForm.name}
                  onChange={(e) => setDogForm({ ...dogForm, name: e.target.value })}
                  required
                  placeholder="Max"
                />
              </div>
              <div className="form-group">
                <label htmlFor="dog-breed">Breed</label>
                <input
                  id="dog-breed"
                  type="text"
                  value={dogForm.breed}
                  onChange={(e) => setDogForm({ ...dogForm, breed: e.target.value })}
                  placeholder="Golden Retriever"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dog-age">Age (years) *</label>
                  <input
                    id="dog-age"
                    type="number"
                    value={dogForm.age}
                    onChange={(e) => setDogForm({ ...dogForm, age: e.target.value })}
                    placeholder="4"
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="dog-weight">Weight (lbs) *</label>
                  <input
                    id="dog-weight"
                    type="number"
                    value={dogForm.weight}
                    onChange={(e) => setDogForm({ ...dogForm, weight: e.target.value })}
                    placeholder="65"
                    min="0.5"
                    step="0.1"
                  />
                  {dogForm.weight && parseFloat(dogForm.weight) < 0.5 && (
                    <div style={{ color: 'red', fontWeight: 600, marginTop: 4, fontSize: '0.9rem' }}>
                      Weight must be at least 0.5 lbs
                    </div>
                  )}
                  {dogForm.weight && parseFloat(dogForm.weight) > 330 && (
                    <div style={{ color: 'red', fontWeight: 600, marginTop: 4, fontSize: '0.9rem' }}>
                      Weight cannot exceed 330 lbs
                    </div>
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dog-body-condition">Body Condition *</label>
                  <select
                    id="dog-body-condition"
                    value={dogForm.body_condition}
                    onChange={(e) => setDogForm({ ...dogForm, body_condition: e.target.value })}
                    className="form-select"
                  >
                    <option value="">Select condition</option>
                    <option value="underweight">Underweight</option>
                    <option value="ideal">Ideal</option>
                    <option value="overweight">Overweight</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="dog-meals">Meals Per Day *</label>
                  <input
                    id="dog-meals"
                    type="number"
                    value={dogForm.meals_per_day}
                    onChange={(e) => setDogForm({ ...dogForm, meals_per_day: e.target.value })}
                    placeholder="2"
                    min="1"
                    max="4"
                  />
                  {dogForm.meals_per_day && (parseInt(dogForm.meals_per_day) < 1 || parseInt(dogForm.meals_per_day) > 4) && (
                    <div style={{ color: 'red', fontWeight: 600, marginTop: 4, fontSize: '0.98rem' }}>
                      Meals per day must be between 1 and 4
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="dog-allergies">Allergies</label>
                <input
                  id="dog-allergies"
                  type="text"
                  value={dogForm.allergies}
                  onChange={(e) => setDogForm({ ...dogForm, allergies: e.target.value })}
                  placeholder="Chicken, beef"
                />
              </div>
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setShowDogModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={!isFormValid() || isSubmittingForm}
                >
                  {editingDog ? 'Save Changes' : 'Add Dog'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* User Profile Edit Modal */}
      {showUserModal && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Profile</h2>
              <button 
                className="modal-close"
                onClick={() => setShowUserModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSaveUser}>
              <div className="form-group">
                <label htmlFor="display-name">Display Name</label>
                <input
                  id="display-name"
                  type="text"
                  value={userForm.display_name}
                  onChange={(e) => setUserForm({ ...userForm, display_name: e.target.value })}
                  placeholder="Your display name"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="first-name">First Name</label>
                  <input
                    id="first-name"
                    type="text"
                    value={userForm.first_name}
                    onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })}
                    placeholder="John"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="last-name">Last Name</label>
                  <input
                    id="last-name"
                    type="text"
                    value={userForm.last_name}
                    onChange={(e) => setUserForm({ ...userForm, last_name: e.target.value })}
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Avatar Icon</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginTop: '8px' }}>
                  {AVATAR_ICONS.map((icon) => (
                    <div
                      key={icon.id}
                      onClick={() => setUserForm({ ...userForm, avatar_icon: icon.id })}
                      style={{
                        padding: '12px',
                        border: userForm.avatar_icon === icon.id ? '3px solid #c14444' : '2px solid #e2e8f0',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: userForm.avatar_icon === icon.id ? '#fffbe6' : '#fff',
                        transition: 'all 0.2s',
                      }}
                      title={icon.label}
                    >
                      <img src={icon.src} alt={icon.label} style={{ width: '32px', height: '32px' }} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Avatar Color</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px', marginTop: '8px' }}>
                  {AVATAR_COLORS.map((color) => (
                    <div
                      key={color}
                      onClick={() => setUserForm({ ...userForm, avatar_color: color })}
                      style={{
                        width: '100%',
                        aspectRatio: '1',
                        backgroundColor: color,
                        border: userForm.avatar_color === color ? '3px solid #c14444' : '2px solid #e2e8f0',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      title={color}
                    >
                      {userForm.avatar_color === color && (
                        <span style={{ fontSize: '20px' }}>✓</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setShowUserModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={isSubmittingForm}
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => {
          setShowPasswordModal(false);
          setPasswordForm({
            current_password: '',
            new_password: '',
            password_confirmation: '',
          });
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Change Password</h2>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordForm({
                    current_password: '',
                    new_password: '',
                    password_confirmation: '',
                  });
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleChangePassword}>
              <div className="form-group">
                <label htmlFor="current-password">Current Password *</label>
                <input
                  id="current-password"
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  placeholder="Enter current password"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="new-password">New Password *</label>
                <input
                  id="new-password"
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  placeholder="Enter new password"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirm-password">Confirm New Password *</label>
                <input
                  id="confirm-password"
                  type="password"
                  value={passwordForm.password_confirmation}
                  onChange={(e) => setPasswordForm({ ...passwordForm, password_confirmation: e.target.value })}
                  placeholder="Confirm new password"
                  required
                />
                {passwordForm.password_confirmation && passwordForm.new_password !== passwordForm.password_confirmation && (
                  <p style={{ color: '#c14444', fontSize: '0.875rem', marginTop: '4px' }}>
                    Passwords do not match
                  </p>
                )}
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordForm({
                      current_password: '',
                      new_password: '',
                      password_confirmation: '',
                    });
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={!isPasswordFormValid() || isSubmittingForm}
                >
                  Save New Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header" style={{ marginBottom: '0' }}>
              <h2>Remove Dog</h2>
              <button 
                className="modal-close"
                onClick={() => setShowDeleteModal(false)}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '20px 0' }}>
              <p style={{ fontSize: '1rem', color: '#333', marginBottom: '20px' }}>
                Are you sure you want to remove this dog from your profile? This action cannot be undone.
              </p>
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn-delete"
                  onClick={confirmDeleteDog}
                  disabled={isSubmittingForm}
                >
                  Remove Dog
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
